import React, { useState } from "react";
import { Stack, Text, Link, Spinner, SpinnerSize, MessageBar, MessageBarType, IconButton } from "@fluentui/react";
import { DataGrid, DataGridColumn } from "@/ui-controls/DataGrid";
import MoreActionsButton from "@/ui-controls/MoreActionsButton";
import AddButton from "@/ui-controls/AddButton";
import { InboundWebhookProvider } from "@/types/webhooks";

interface Props {
  providers: InboundWebhookProvider[];
  isLoading?: boolean;
  error?: string | null;
  onDelete?: (provider: InboundWebhookProvider) => void;
  onEdit?: (provider: InboundWebhookProvider) => void;
  onAdd?: () => void;
  onRefresh?: () => void;
}

enum MenuActionType {
  DELETE_PROVIDER = 1,
  EDIT_PROVIDER = 2,
}

const ProviderConfiguration = (props: Props) => {
  const [selectedProvider, setSelectedProvider] = useState<InboundWebhookProvider | undefined>(undefined);
  const [menuActionType, setMenuActionType] = useState<MenuActionType | undefined>(undefined);

  const columns: DataGridColumn[] = [
    {
      key: "id",
      name: "Id",
      fieldName: "Id",
      minWidth: 50,
      maxWidth: 80,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "description",
      name: "Description",
      fieldName: "Description",
      minWidth: 200,
      maxWidth: 300,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "providerUrl",
      name: "Provider URL",
      fieldName: "ProviderUrl",
      minWidth: 200,
      maxWidth: 400,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: InboundWebhookProvider) => (
        <Text variant="small" styles={{ root: { color: "#605e5c" } }}>
          {item.ProviderUrl || "-"}
        </Text>
      ),
    },
    {
      key: "securityType",
      name: "Security Type",
      fieldName: "InboundWebhookSecurityTypeId",
      minWidth: 120,
      maxWidth: 150,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: InboundWebhookProvider) => {
        let securityType = "Unknown";
        if (item.IsApiKeyType()) {
          securityType = "API Key";
        } else if (item.IsOAuth2Type()) {
          securityType = "OAuth2";
        } else if (item.IsSvixType()) {
          securityType = "Svix";
        }
        return (
          <Text variant="small" styles={{ root: { fontWeight: "600" } }}>
            {securityType}
          </Text>
        );
      },
    },
    {
      key: "actions",
      name: "",
      minWidth: 50,
      maxWidth: 70,
      onRender: (item: InboundWebhookProvider) => (
        <MoreActionsButton
          menu={[
            {
              key: "edit",
              text: "Edit provider",
              iconProps: { iconName: "Edit" },
              onClick: () => {
                if (props.onEdit) {
                  props.onEdit(item);
                }
              },
            },
            {
              key: "delete",
              text: "Delete provider",
              iconProps: { iconName: "Delete" },
              onClick: () => {
                if (props.onDelete) {
                  props.onDelete(item);
                }
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Stack styles={{ root: { height: "100%", padding: "16px 0" } }}>
      {props.error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          styles={{ root: { marginBottom: "16px" } }}
        >
          {props.error}
        </MessageBar>
      )}
      
      {props.isLoading ? (
        <Stack 
          horizontalAlign="center" 
          verticalAlign="center" 
          styles={{ root: { height: "200px" } }}
        >
          <Spinner size={SpinnerSize.large} label="Loading webhook providers..." />
        </Stack>
      ) : props.providers.length === 0 ? (
        <Stack 
          horizontalAlign="center" 
          verticalAlign="center" 
          tokens={{ childrenGap: 16 }}
          styles={{ root: { height: "200px" } }}
        >
          <Text variant="medium" styles={{ root: { color: "#605e5c" } }}>
            No webhook providers configured
          </Text>
          <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="center">
            <Text variant="small" styles={{ root: { color: "#8a8886" } }}>
              Start by
            </Text>
            <Link 
              onClick={() => {
                if (props.onAdd) {
                  props.onAdd();
                }
              }}
              styles={{ 
                root: { 
                  fontSize: 14,
                  fontWeight: "600",
                  color: "#0078d4",
                  textDecoration: "none",
                  cursor: "pointer",
                  ":hover": {
                    textDecoration: "underline",
                    color: "#106ebe"
                  }
                } 
              }}
            >
              creating a new provider
            </Link>
          </Stack>
        </Stack>
      ) : (
        <Stack styles={{ root: { height: "100%" } }}>
          {/* Action Buttons */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px", marginBottom: "16px" }}>
            <AddButton
              text="Add Provider"
              onClick={() => {
                if (props.onAdd) {
                  props.onAdd();
                }
              }}
            />
            <IconButton
              iconProps={{ iconName: "Refresh" }}
              title="Refresh"
              ariaLabel="Refresh"
              onClick={() => {
                if (props.onRefresh) {
                  props.onRefresh();
                }
              }}
              disabled={props.isLoading}
              styles={{
                root: { width: 40, height: 40 },
                rootHovered: { backgroundColor: "#f3f2f1" },
                icon: {
                  fontSize: 18,
                  color: props.isLoading ? "#a19f9d" : "#0078d4",
                },
                rootPressed: { backgroundColor: "#e1dfdd" },
                rootDisabled: { backgroundColor: "transparent" },
              }}
            />
          </div>
          
          {/* Providers Grid */}
          <DataGrid items={props.providers} columns={columns} />
        </Stack>
      )}
    </Stack>
  );
};

export default ProviderConfiguration;
